/**
 * 036 (issue 207) — the ONE definition of "coordinate across windows through `navigator.locks`,
 * or run uncoordinated with a one-time warning when it does not exist" (jsdom has no
 * `navigator.locks`, hence the seam this whole file exists to provide).
 *
 * This used to be written out twice — once in `core/session/session.store.ts` for the renewal
 * lock, once in `core/outbox/outbox.store.ts` for the send-loop lock — with its own `warned` flag
 * and its own test seam in each place. Two copies of the same fallback behaviour is exactly the
 * kind of second definition CLAUDE.md's own lessons warn can drift silently, so this is now a
 * FACTORY: each caller gets its OWN runner instance (`createLockRunner()`), with its own `warned`
 * flag and its own test seam, but the fallback logic itself is written once.
 *
 * A factory rather than one shared singleton runner deliberately: session renewal and the
 * outbox's send loop are coordinated by DIFFERENT lock names for DIFFERENT purposes, and a test
 * that installs a custom runner for one (to gate or observe it) must not silently also govern the
 * other's calls in the same test run.
 */

export type LockRunner = <T>(name: string, fn: () => Promise<T>) => Promise<T>;

interface MinimalLockManager {
  request(name: string, callback: () => Promise<unknown> | unknown): Promise<unknown>;
}

export interface LockRunnerHandle {
  /** Runs `fn` under `navigator.locks`, or directly (with a one-time warning) where it does not exist. */
  readonly withLock: LockRunner;
  /** Test seam. Production code never calls it. */
  readonly setLockRunner: (next: LockRunner | null) => void;
}

/**
 * `warningContext` names what could not be coordinated (e.g. "outbox: sending", "session:
 * renewing") — the whole reason this is a factory and not one shared function is that each
 * caller's warning should still say what IT is doing without needing the lock NAME parsed back
 * out of a shared message.
 */
export function createLockRunner(warningContext: string): LockRunnerHandle {
  let warned = false;

  const defaultRunner: LockRunner = async (name, fn) => {
    const locks = (globalThis as { navigator?: { locks?: MinimalLockManager } }).navigator?.locks;
    if (locks && typeof locks.request === 'function') {
      return (await locks.request(name, () => fn())) as Awaited<ReturnType<typeof fn>>;
    }
    if (!warned) {
      warned = true;
      console.warn(`${warningContext}: navigator.locks is unavailable; proceeding without cross-window coordination`);
    }
    return fn();
  };

  let current: LockRunner = defaultRunner;

  return {
    withLock: (name, fn) => current(name, fn),
    setLockRunner: (next) => {
      current = next ?? defaultRunner;
      warned = false;
    },
  };
}
